import * as React from 'react'
import { useEffect } from 'react'
import Icon, { tourTypeToIcon } from '../../../common/src/components/Icon'
import { Spacer } from '../../../common/src/components/core'
import { FlexDiv } from '../../../common/src/components/layout'
import { appContext } from '../../../common/src/context'
import {
  dateFmtWeekday,
  dateMDShortFmt,
  dateNoYearFmtLong,
  isoDateFmt,
  startOfDay,
  getTimeString,
  diffMins,
  isBefore,
  maxDate,
  compareDates,
  isAfter,
  isoSecondsFmt,
  addMinutes,
  timeFmt,
  getTimeAgo,
  dateNoYearFmt,
} from '../../../common/src/date'
import { nPlural, capitalize, formatPhoneNumber } from '../../../common/src/format'
import { getWindow } from '../../../common/src/global'
import { showingStatusSort } from '../../../common/src/sort'
import {
  FetchAgentCalendarQuery,
  FetchAgentCalendarQueryVariables,
  CalResponseStatus,
  TourType,
  ConfirmationStatus,
  LeasePropertyType,
  UnitType,
  MessageType,
} from '../../../common/src/types.gen'
import { createRequestScopedClient } from '../apolloClientForLambda'
import { Nbsp } from '../emails/components'
import { createWidget } from '../widgets/base'
import CalendarModal, {
  ShowingTourType,
  ModalDateTime,
  ModalFirstTitle,
  ModalSecondTitle,
  PropertyAddress,
  UnitInfo,
  PropertyInfo,
  IconAndTextWrapper,
  iconBoxClass,
  Img,
  declinedClass,
  ModalDetails,
  freeBusyIconClass,
  SmallNote,
} from './CalendarModal'
import {
  AgentCalendarBodyRoot,
  AgentCalendarContainer,
  AgentCalendarGrid,
  AgentCalendarHeaderRoot,
  AgentCalendarLabels,
  CalendarDownArrow,
  calendarElementClass,
  CalendarLabel,
  CurrentTimeBar,
  DateHeader,
  DateHeaderMonthDay,
  DateHeaderWeekday,
  DatePicker,
  ElementsAndLabels,
  hourElementClass,
  HourElementSeparatorRoot,
  HourElementText,
  labelsWrapperClass,
  Link,
  NameEmailHeader,
  ShowingElement,
  ShowingElementsRoot,
  ShowingText,
  StrikethroughSpan,
  syncButtonClass,
  SyncHeader,
  TourTypeIconRoot,
  UNIT_HEIGHT,
  wrapCalElementClass,
} from './agentCalendarStyles'
import { fetchAgentCalendarQuery } from './fetchAgentCalendar'

export interface AgentCalendarProps {
  agentCalendar: FetchAgentCalendarQuery['agentCalendar']
  uuid: string
}

export const agentCalendarWidgetId = 'agent-calendar-page'
const agentCalendarWidget = createWidget(agentCalendarWidgetId, (props: AgentCalendarProps) => (
  <AgentCalendarPage {...props} />
))

/**
 * Server-side function for rendering agent calendar widget HTML.
 *
 * Makes GraphQL API requests to appserver API to get calendar data for the provided UUID.
 */
export async function getAgentCalendarHtml(id: string, date?: string) {
  const client = createRequestScopedClient()
  const res = await client.query<FetchAgentCalendarQuery, FetchAgentCalendarQueryVariables>({
    query: fetchAgentCalendarQuery,
    variables: {
      id,
      date,
    },
  })

  if (res.data == null) {
    return res.error?.message || 'calendar not available'
  }

  const props = { agentCalendar: res.data.agentCalendar, uuid: id }
  return getAgentCalendarHtmlForProps(props, props)
}

/**
 * Returns the agent calendar widget HTML provided data and (optional) apollo cache state from requesting the data.
 */
export function getAgentCalendarHtmlForProps(props: AgentCalendarProps, apolloState?: any) {
  const pageTitle = props.agentCalendar.contact.displayName
  return agentCalendarWidget.renderHtml(pageTitle, props, apolloState)
}

export function AgentCalendarPage(props: AgentCalendarProps) {
  const { agentCalendar: calendarData } = props
  const name = calendarData.contact.displayName
  const emailAddress = calendarData.contact.addressEmail
  const dayOfWeek = dateFmtWeekday(calendarData.date)
  const monthDay = dateMDShortFmt(calendarData.date)

  return (
    <>
      <AgentCalendarContainer>
        <AgentCalendarHeader
          name={name}
          emailAddress={emailAddress}
          dayOfWeek={dayOfWeek}
          monthDay={monthDay}
          date={calendarData.date}
          uuid={props.uuid}
        />
        <AgentCalendarBody agentCalendar={calendarData} uuid={props.uuid} />
      </AgentCalendarContainer>
    </>
  )
}

function syncNow(uuid: string) {
  return () =>
    fetch('/api/single_calendar_sync', {
      method: 'POST',
      body: JSON.stringify({
        id: uuid,
      }),
      headers: {
        'Content-Type': 'application/json',
      },
    })
      .then(response => response.json())
      .then(() => location.reload())
      .catch(err => {
        console.log(err)
        location.reload()
      })
}

interface AgentCalendarHeaderProps {
  name: string
  emailAddress?: string | null
  dayOfWeek: string
  monthDay: string
  date: string
  uuid: string
}

/**
 * Top of the page:  Thu Mar 12  [agent name] [agent email]
 */
function AgentCalendarHeader(props: AgentCalendarHeaderProps) {
  const date = new Date(props.date)
  const id = props.uuid

  useEffect(() => {
    initJqueryCalendar(date, id)
  }, []) // only run on initial render

  return (
    <AgentCalendarHeaderRoot>
      <DateHeader>
        <Spacer width={8} />
        <DateHeaderWeekday dayOfWeek={props.dayOfWeek}></DateHeaderWeekday>
        <Spacer width={8} />
        <DateHeaderMonthDay monthDay={props.monthDay}></DateHeaderMonthDay>
        <Spacer width={8} />
        {/* hack: datepicker input is on top of the arrow but has a 0 opacity so it's not visible but can still be clicked on. */}
        <div>
          <CalendarDownArrow isDown={false} color="midway"></CalendarDownArrow>
          <DatePicker id="datepicker" />
        </div>
      </DateHeader>
      <NameEmailHeader name={props.name} emailAddress={props.emailAddress} />
      <SyncHeader>
        <div className={syncButtonClass} onClick={syncNow(props.uuid)}>
          Sync
        </div>
      </SyncHeader>
    </AgentCalendarHeaderRoot>
  )
}

/**
 * Initializes and sets up the datepicker calendar.
 */
function initJqueryCalendar(date: Date, id: string) {
  const window = getWindow() as any
  if (window && window.$) {
    const datePicker = window.$('#datepicker')
    datePicker.datepicker({
      // agent's "today" is the default day
      defaultDate: date,
      // go to the agent calendar for chosen date
      onSelect(d: string) {
        window.location.href = '/api/agent_calendar?id=' + id + '&date=' + isoDateFmt(startOfDay(new Date(d)))
      },
    })
  }
}

const busyStatus = [CalResponseStatus.ACCEPTED, CalResponseStatus.TENTATIVE]
const freeStatus = [CalResponseStatus.DECLINED, CalResponseStatus.NEEDS_ACTION, CalResponseStatus.UNKNOWN]

/**
 * Returns [1,2,3,...,24]
 */
function getRange(num: number) {
  const ret = []
  for (let i = 1; i <= num; i++) {
    ret.push(i)
  }
  return ret
}

/**
 * Layout the calendar grid + events. Guarantees no overlapping showings. Showings overlap every other element type.
 */
function AgentCalendarBody(props: AgentCalendarProps) {
  const dayBeginning = props.agentCalendar.startOfDay
  const dayEnd = props.agentCalendar.endOfDay
  const emailAddress = props.agentCalendar.contact.addressEmail || ''
  const busyEvents = getBusyOrFreeEvents(
    props.agentCalendar.showings,
    props.agentCalendar.calEvents,
    emailAddress,
    busyStatus
  )
  const freeEvents = getBusyOrFreeEvents(
    props.agentCalendar.showings,
    props.agentCalendar.calEvents,
    emailAddress,
    freeStatus
  )

  const showingElements = getShowingElements(
    props.agentCalendar.showings,
    props.agentCalendar.calEvents,
    dayBeginning,
    dayEnd,
    props.agentCalendar.contact.id,
    props.agentCalendar.timeZone
  )

  const consolidatedFreeEvents = consolidateOverlappingEvents(freeEvents)
  const consolidatedBusyEvents = consolidateOverlappingEvents(busyEvents)

  return (
    <AgentCalendarBodyRoot>
      <ElementsAndLabels>
        <AgentCalendarGrid>
          <ShowingElementsRoot>{showingElements}</ShowingElementsRoot>
          {getRange(24).map(i => (
            <HourElement key={i} time={getTimeString(i)} hour={i}></HourElement>
          ))}
          {props.agentCalendar.workHours.map((wh, i) =>
            getCalendarElement(i, wh.lowerBound, wh.upperBound, ElemType.WORKING, dayBeginning, dayEnd)
          )}
          {props.agentCalendar.offHours.map((wh, i) =>
            getCalendarElement(i, wh.lowerBound, wh.upperBound, ElemType.OFF_HOURS, dayBeginning, dayEnd)
          )}
          {consolidatedBusyEvents.map((b, i) =>
            getCalendarElement(
              i,
              b.timeStart,
              b.timeEnd,
              ElemType.BLOCKED,
              dayBeginning,
              dayEnd,
              props.agentCalendar.timeZone,
              busyEvents
            )
          )}
          {consolidatedFreeEvents.map((b, i) =>
            getCalendarElement(
              i,
              b.timeStart,
              b.timeEnd,
              ElemType.FREE,
              dayBeginning,
              dayEnd,
              props.agentCalendar.timeZone,
              freeEvents
            )
          )}
          <CurrentTimeBar date={props.agentCalendar.date} isToday={props.agentCalendar.isToday} />
        </AgentCalendarGrid>
        {getLabels(
          consolidatedBusyEvents,
          consolidatedFreeEvents,
          props.agentCalendar.startOfDay,
          props.agentCalendar.timeZone
        )}
      </ElementsAndLabels>
      <AgentCalendarLabels>&nbsp;</AgentCalendarLabels>
    </AgentCalendarBodyRoot>
  )
}

/**
 * Label all the blocked/free sections to help understand what each part of the calendar represents.
 */
function getLabels(
  busyEvents: FetchAgentCalendarQuery['agentCalendar']['calEvents'],
  freeEvents: FetchAgentCalendarQuery['agentCalendar']['calEvents'],
  dayBeginning: string,
  zone: string
) {
  const filteredFreeEvents = []
  for (const freeEvent of freeEvents) {
    const diffs = busyEvents.map(e => Math.abs(diffMins(e.timeStart, freeEvent.timeStart)))
    // only include this free event if it doesn't start within 15 minutes of the start of a busy event to avoid overlap
    if (diffs.find(d => d < 15) === undefined) {
      filteredFreeEvents.push(freeEvent)
    }
  }
  return (
    <div className={labelsWrapperClass}>
      {busyEvents.map((b, i) => getLabel(i, b.timeStart, ElemType.BLOCKED, dayBeginning, zone, b.source))}
      {filteredFreeEvents.map((f, i) => getLabel(i, f.timeStart, ElemType.FREE, dayBeginning, zone, f.source))}
    </div>
  )
}

function getLabel(
  key: number,
  time: string,
  type: ElemType,
  dayBeginning: string,
  zone: string,
  source?: string | null
) {
  const minsFromStartOfDay = diffMins(time, dayBeginning)
  const top = UNIT_HEIGHT * minsFromStartOfDay
  const now = new Date()
  const opacity = isBefore(now, time) ? 1 : 0.5
  switch (type) {
    case ElemType.WORKING:
      return (
        <CalendarLabel
          text={'Work hours start at ' + timeFmt(time, zone).replace(':00', '')}
          top={top}
          key={key}
          opacity={opacity}
        ></CalendarLabel>
      )
    case ElemType.OFF_HOURS:
      return (
        <CalendarLabel
          text={'Work hours end at ' + timeFmt(time, zone).replace(':00', '')}
          top={top}
          key={key}
          opacity={opacity}
        ></CalendarLabel>
      )
    case ElemType.BLOCKED:
    case ElemType.FREE:
      return (
        <CalendarLabel
          text={'in ' + getEventSrc(source)}
          top={top + 6}
          key={key}
          status={type}
          opacity={opacity}
        ></CalendarLabel>
      )
    default:
      return <></>
  }
}

/**
 * Consolidates overlapping calendar events into a single event.
 */
export function consolidateOverlappingEvents(events: FetchAgentCalendarQuery['agentCalendar']['calEvents']) {
  if (events.length <= 1) {
    return events
  }

  // sort events based on their start time
  const sorted = sortCalEvents(events)

  const res = []
  res.push(sorted[0])
  for (let i = 1; i < sorted.length; i++) {
    const prevEvent: FetchAgentCalendarQuery['agentCalendar']['calEvents'][0] | undefined = res.pop()
    if (!prevEvent) {
      break
    }
    const currEvent = sorted[i]
    if (prevEvent && eventsOverlap(prevEvent, currEvent)) {
      // if this event and the previous event overlap, create a new event that consolidates the two
      const consolidated: FetchAgentCalendarQuery['agentCalendar']['calEvents'][0] = {
        __typename: 'CalEvent',
        timeCreated: prevEvent.timeCreated,
        timeStart: prevEvent.timeStart,
        timeEnd: maxDate(currEvent.timeEnd, prevEvent.timeEnd).toString(),
        summary: null,
        showingId: null,
        attendees: prevEvent.attendees.concat(currEvent.attendees),
        source: prevEvent.source,
      }
      res.push(consolidated)
    } else {
      // otherwise keep the two events separate
      res.push(prevEvent)
      res.push(currEvent)
    }
  }
  return res
}

/**
 * Returns sorted events based on increasing order of start time.
 */
export function sortCalEvents(events: FetchAgentCalendarQuery['agentCalendar']['calEvents']) {
  return events.slice().sort((a, b) => compareDates(a.timeStart, b.timeStart))
}

/**
 * Returns true if events a and b overlap with each other and false otherwise, assuming that event a has a start time before event b.
 *
 * Here we consider two events that are back-to-back as overlapping events.
 */
export function eventsOverlap(
  a: FetchAgentCalendarQuery['agentCalendar']['calEvents'][0],
  b: FetchAgentCalendarQuery['agentCalendar']['calEvents'][0]
): boolean {
  const compare = compareDates(b.timeStart, a.timeEnd)
  if (compare <= 0) {
    return true
  }
  return false
}

/**
 * Returns a contact's calEvents in which the contact attendee has the specified response status.
 */
function getBusyOrFreeEvents(
  showings: FetchAgentCalendarQuery['agentCalendar']['showings'],
  calEvents: FetchAgentCalendarQuery['agentCalendar']['calEvents'],
  attendeeEmailAddress: string,
  responseStatus: CalResponseStatus[]
) {
  const res = []
  for (const event of calEvents) {
    const agentAttendee = event.attendees.find(a => a.calId === attendeeEmailAddress)
    // skip displaying calendar blocks that are actually just showings on the agent's hosted cal
    const matchingShowing = showings.find(s => event.showingId === s.id)
    if (
      matchingShowing == null &&
      agentAttendee &&
      responseStatus.includes(agentAttendee.responseStatus) &&
      !event.showingId
    ) {
      res.push(event)
    }
  }
  return res
}

/**
 * Given the data for a Lisa showing or GSuite/Exchange calendar block, compute its layout.
 * Each type of calendar element (see: ElemType) has a distinct background so the user can tell them apart.
 * This function also handles the sizing of each element. We scale y-position and element height to the
 * calendar grid, proportional to the element's start and stop time.
 */
function getCalendarElement(
  key: number,
  startTime: string,
  endTime: string,
  blockType: ElemType,
  dayBeginning: string,
  dayEnd: string,
  timeZone?: string,
  allCalEvents?: FetchAgentCalendarQuery['agentCalendar']['calEvents'],
  emailProvider?: string | null,
  title?: JSX.Element,
  tourType?: TourType,
  strikethrough?: boolean,
  width?: string, // override normal width calc
  left?: string, // override normal offset from left edge
  showing?: FetchAgentCalendarQuery['agentCalendar']['showings'][0],
  agentId?: number
) {
  // replace slashes
  startTime = removeSlashes(startTime)
  endTime = removeSlashes(endTime)

  // do not let element surpass day view
  if (isAfter(endTime, dayEnd)) {
    endTime = dayEnd
  }
  if (isAfter(dayBeginning, startTime)) {
    startTime = dayBeginning
  }

  const timeLength = diffMins(endTime, startTime)
  const elemHeight = UNIT_HEIGHT * timeLength
  const elemWidth = width || 'calc(100% - 60px)'
  const minsFromStartOfDay = diffMins(startTime, dayBeginning)
  const top = UNIT_HEIGHT * minsFromStartOfDay
  const hour = Math.floor(minsFromStartOfDay / 60) + 1
  const stripesTileUrl = 'url(https://dynasty-images.s3-us-west-1.amazonaws.com/stripes-tile.png)'
  const opacity = getElementOpacity(blockType, endTime) // past events, cal blocks look faded

  let backgroundColor
  let backgroundImage = 'none'
  let innerElem: JSX.Element
  let zIndex: number
  let cursor = ''

  const [showModal, setShowModal] = React.useState(false)
  const onClick = React.useCallback(() => {
    setShowModal(true)
  }, [])
  const onClose = React.useCallback(() => {
    setShowModal(false)
  }, [])

  if (blockType === ElemType.SHOWING) {
    innerElem = (
      <div>
        <ShowingElement
          elemHeight={elemHeight}
          top={top}
          elemWidth={elemWidth}
          zIndex={4}
          strikethrough={strikethrough}
          opacity={opacity}
          left={left}
          onClick={onClick}
        >
          <TourTypeIconRoot>{tourTypeToIcon(tourType || TourType.IN_PERSON, 'silver')}</TourTypeIconRoot>
          <Spacer width={12} />
          <ShowingText>{title}</ShowingText>
        </ShowingElement>
        {showModal && showing && timeZone && (
          <CalendarModal top={'20%'} onClose={onClose} title="calendar modal">
            {getShowingModalChildren(showing, timeZone, agentId, emailProvider, strikethrough)}
          </CalendarModal>
        )}
      </div>
    )
  } else {
    switch (blockType) {
      case ElemType.OFF_HOURS:
        backgroundColor = 'rgba(16, 16, 17, 0.25)'
        backgroundImage = 'none'
        zIndex = 0 // off + working hours lowest in z-stack
        break
      case ElemType.WORKING:
        backgroundColor = 'white'
        zIndex = 0 // off + working hours lowest in z-stack
        break
      case ElemType.FREE:
        backgroundColor = '#00e08f'
        backgroundImage = stripesTileUrl
        zIndex = 2 // "free" blocks next: it's OK if they're hidden by showings/busy blocks
        cursor = 'pointer'
        break
      case ElemType.BLOCKED:
        backgroundColor = '#c04a6e'
        backgroundImage = stripesTileUrl
        zIndex = 3 // "busy" blocks next: should be hidden only by Lisa showings
        cursor = 'pointer'
        break
    }
    innerElem = (
      <div>
        <div
          onClick={onClick}
          className={calendarElementClass({
            backgroundColor,
            backgroundImage,
            elemHeight,
            top,
            elemWidth,
            zIndex,
            cursor,
            opacity,
            hour,
          })}
        />
        {showModal && timeZone && (
          <CalendarModal top="20%" onClose={onClose} title="calendar modal">
            {getFreeBusyModalChildren(startTime, endTime, timeZone, blockType, dayBeginning, dayEnd, allCalEvents)}
          </CalendarModal>
        )}
      </div>
    )
  }

  return (
    <FlexDiv key={key}>
      <div className={wrapCalElementClass}>{innerElem}</div>
    </FlexDiv>
  )

  /**
   * Make sure all past events (ending before "now") look faded.
   * (Except for blocks of working hours: those have a white background,
   * so we'll always want to display that background at full opacity).
   */
  function getElementOpacity(type: ElemType, time: string) {
    const now = new Date()
    if (type === ElemType.WORKING || isBefore(now, time)) {
      return 1
    }
    return 0.5
  }
}

function removeSlashes(str: string) {
  return str.replace('\\', '').replace('/', '')
}

export interface CalendarElementProps {
  backgroundColor?: string
  backgroundImage?: string
  elemHeight: number
  top: number
  elemWidth: number | string
  zIndex: number
  cursor?: string
  opacity?: number
  children?: React.ReactNode
  hour?: number
  strikethrough?: boolean
  left?: string // override normal offset from left edge
  onClick?: (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void
}

// "might attend" statuses
const mightAttendStatus = [
  ConfirmationStatus.OFFERED,
  ConfirmationStatus.ACCEPTED,
  ConfirmationStatus.CONFIRM_SENT,
  ConfirmationStatus.CONFIRMED,
]
// also show prospects we canceled on b/c the agent was a no-show
const canceledStatus = [ConfirmationStatus.CANCEL_BY_US]
const willAttendStatus = [ConfirmationStatus.ACCEPTED, ConfirmationStatus.CONFIRM_SENT, ConfirmationStatus.CONFIRMED]

/**
 * Title of Lisa showing on the calendar grid before its clicked, e.g. —
 *
 * "JOHN + 1 · Stone Lodge · BR1 showing"
 */
function getShowingText(
  prospects: FetchAgentCalendarQuery['agentCalendar']['showings'][0]['showingContacts'],
  unitType: string | null | undefined,
  taskDisplayName: string,
  isOnAgentCalendar: boolean
) {
  const attendingProspect = getMostLikelyProspect(prospects)
  const acceptedProspects = prospects.filter(p => willAttendStatus.includes(p.confirmationStatus))
  const numOthers = acceptedProspects.length - 1
  const otherStr = numOthers > 0 ? ' + ' + numOthers : ''
  const name =
    attendingProspect?.contact.firstName === null
      ? nPlural(prospects.length, 'prospect')
      : attendingProspect?.contact.firstName + otherStr
  return (
    <StrikethroughSpan strikethrough={!isOnAgentCalendar}>
      {name?.toLocaleUpperCase()}
      {separator}
      {taskDisplayName}
      {separator}
      {unitType}
      {' showing'}
    </StrikethroughSpan>
  )
}

/**
 * From a list of Lisa showings, generate black/white rounded rects with a title containing showing info. Each looks like:
 *
 * [ JESUS  ·  The Villas  ·  BR1 showing ]
 *
 * The showings this returns won't overlap on the calendar grid (just like Google Cal, Outlook, etc). Layout works like this:
 *
 * see: https://stackoverflow.com/a/28231581
 */
// eslint-disable-next-line sonarjs/cognitive-complexity
function getShowingElements(
  showings: FetchAgentCalendarQuery['agentCalendar']['showings'],
  calEvents: FetchAgentCalendarQuery['agentCalendar']['calEvents'],
  dayBeginning: string,
  dayEnd: string,
  contactId: number,
  timeZone: string
) {
  // Sort showings by start time, then by ending time
  const sorted = sortShowingsEarliestFirst(showings)

  // Step (1): Think of an unlimited grid with just a left edge.
  let columns: FetchAgentCalendarQuery['agentCalendar']['showings'][] = []
  let ret: JSX.Element[] = []
  let lastEventEnding = null

  /**
   * Step (2) + (3): calc widths for overlapping showings
   * Don't worry about height and vertical position for now. To figure out the width, think of our empty grid.
   * We'll try to place each showing in a column as far left as possible, without it intersecting any earlier showing in that column.
   * So if you have three showings, of which two overlap in time ...
   *
   * showingA [ 10am - 11am ]
   * showingB [ 10am - 11am ]
   * showingC [ 11am - 12pm ]
   *
   * ... you'll pass through this loop for all three showings, but only twice will you output JSX elements to the ret.
   * In the first pass through the loop, you'll get {@code columns} that look like this:
   *
   * columns[0] == [ showingA ] (alone in a list)
   * columns[1] == [ showingB ] (alone in a list)
   *
   * ... which get hydrated and added to the ret. In the next (and last) pass, you'll see columns like ...
   *
   * columns[0] == [ showingC ] (alone in a list)
   *
   * Most impt thing: we clear {@code columns} when we're not actively laying out overlapping showings. Columns just count how many
   * overlapping showings will need tricky layout. It's more like "party size" than like "count of grid columns in a spreadsheet"
   */
  for (const s of sorted) {
    const timeEnd = isoSecondsFmt(addMinutes(s.timeStart, getShowingDurationMins(s.leasingTask)))

    const showAgentUnavailShowings = false // configurable: should we show showings where the agent is marked NOT_AVAIL?

    if (showAgentUnavailShowings && wasAgentUnavailable(s, contactId)) {
      // skip display if the agent was marked NOT_AVAIL: they don't need to be hassled about these
      continue
    }

    if (getPlannedToAttendProspects(s)?.length < 1) {
      // skip trying to place any showing with no attending (or attending, then canceled) prospects
      continue
    }

    const mostLikelyProspect = getMostLikelyProspect(s.showingContacts)
    if (!mostLikelyProspect || !willAttendStatus.includes(mostLikelyProspect.confirmationStatus)) {
      // if the most-likely-to-attend prospect is not confirmed, skip displaying the showing
      continue
    }

    if (lastEventEnding !== null && isAfter(s.timeStart, lastEventEnding)) {
      // The latest showing is later than any of the showings in the
      // current row. There is no overlap. Output the current
      // showing row and start a new showing row.
      const expanded = expandShowingsHorizontally(columns, calEvents, dayBeginning, dayEnd, timeZone, contactId)
      ret = ret.concat(expanded?.slice())
      columns = [] // This starts a new set of showings, each with its own column
      lastEventEnding = null
    }
    // Try to place the showing inside an existing column
    let placed = false
    for (const col of columns) {
      if (!collidesWith(col[col.length - 1], s)) {
        col.push(s)
        placed = true
        break
      }
    }

    // It was not possible to place the showing in an existing column, so we must need a new one
    // Add this element as the first in a new column of its own
    if (!placed) {
      columns.push([s])
    }

    // Remember the latest event end time of the current group of connectedShowings
    // This is later used to determine if a new groups starts.
    if (lastEventEnding === null || isAfter(timeEnd, lastEventEnding)) {
      lastEventEnding = timeEnd
    }
  }

  /**
   * Step (4): Then, when all events are placed into columns, compute final widths
   */
  if (columns.length > 0) {
    const expanded = expandShowingsHorizontally(columns, calEvents, dayBeginning, dayEnd, timeZone, contactId)
    ret = ret.concat(expanded?.slice())
  }

  return ret
}

/**
 * Sort showings first by start time, then by end time. Uses actual end time as calc'd from task/firm data.
 */
function sortShowingsEarliestFirst(showings: FetchAgentCalendarQuery['agentCalendar']['showings']) {
  return showings
    .slice()
    .sort(
      (
        a: FetchAgentCalendarQuery['agentCalendar']['showings'][0],
        b: FetchAgentCalendarQuery['agentCalendar']['showings'][0]
      ) => {
        const aTimeEnd = addMinutes(a.timeStart, getShowingDurationMins(a.leasingTask))
        const bTimeEnd = addMinutes(b.timeStart, getShowingDurationMins(b.leasingTask))
        if (isBefore(a.timeStart, b.timeStart)) {
          return -1
        }
        if (isAfter(a.timeStart, b.timeStart)) {
          return 1
        }
        if (isBefore(aTimeEnd, bTimeEnd)) {
          return -1
        }
        if (isAfter(aTimeEnd, bTimeEnd)) {
          return 1
        }
        return 0
      }
    )
}

/**
 * Step (4) of {@link #getShowingElements}: returns non overlapping {@link #CalendarElement}s
 * for each with the final computed width and position on the calendar grid.
 */
function expandShowingsHorizontally(
  columns: FetchAgentCalendarQuery['agentCalendar']['showings'][],
  calEvents: FetchAgentCalendarQuery['agentCalendar']['calEvents'],
  dayBeginning: string,
  dayEnd: string,
  timeZone: string,
  agentId: number
) {
  const ret: JSX.Element[] = []
  const n = columns.length

  columns.map((col, i) => {
    col.map(showing => {
      const colSpan = getMaxColumnSpan(showing, i, columns)
      const elemWidth = Math.floor((colSpan / n) * 100) + '%'
      const elemLeftOffset = (i / n) * 100 + '%'
      const timeEnd = isoSecondsFmt(addMinutes(showing.timeStart, getShowingDurationMins(showing.leasingTask)))
      const emailProvider = getEventSrc(calEvents[0]?.source)
      const isOnAgentCalendar = calEvents.some(e => e.showingId === showing.id) // if matching cal event the agent hasn't declined showing
      const attendingProspects = getPlannedToAttendProspects(showing)

      const title = getShowingText(
        attendingProspects,
        showing.unitType,
        showing.leasingTask.displayName,
        isOnAgentCalendar
      )
      ret.push(
        getCalendarElement(
          showing.id,
          showing.timeStart,
          timeEnd,
          ElemType.SHOWING,
          dayBeginning,
          dayEnd,
          timeZone,
          [],
          emailProvider,
          title,
          showing.tourType || undefined,
          !isOnAgentCalendar,
          elemWidth,
          elemLeftOffset,
          showing,
          agentId
        )
      )
    })
  })

  return ret
}

/**
 * Checks if two showings overlap in time
 */
function collidesWith(
  a: FetchAgentCalendarQuery['agentCalendar']['showings'][0],
  b: FetchAgentCalendarQuery['agentCalendar']['showings'][0]
): boolean {
  const aTimeEnd = addMinutes(a.timeStart, getShowingDurationMins(a.leasingTask))
  const bTimeEnd = addMinutes(b.timeStart, getShowingDurationMins(b.leasingTask))
  return isAfter(aTimeEnd, b.timeStart) && isBefore(a.timeStart, bTimeEnd)
}

/**
 * Expand showings at the far right to use up any remaining space.
 * Checks how many columns the showing can expand into without
 * colliding with other showings. Step 5 in the algorithm.
 */
function getMaxColumnSpan(
  showing: FetchAgentCalendarQuery['agentCalendar']['showings'][0],
  iColumn: number,
  rows: FetchAgentCalendarQuery['agentCalendar']['showings'][]
) {
  let colSpan = 1

  // eslint-disable-next-line @typescript-eslint/prefer-for-of
  for (let i = iColumn + 1; i < rows.length; i++) {
    const col = rows[i]
    for (const otherShowing of col) {
      if (collidesWith(showing, otherShowing)) {
        return colSpan
      }
    }
    colSpan++
  }
  return colSpan
}

/**
 * For a showing, get only those showing contacts who are:
 * - prospects
 * - might attend
 * - were gonna come, but the agent / lisa canceled on them
 *
 * If no prospects exist, returns `undefined`
 */
function getPlannedToAttendProspects(
  showing: FetchAgentCalendarQuery['agentCalendar']['showings'][0]
): FetchAgentCalendarQuery['agentCalendar']['showings'][0]['showingContacts'] {
  return showing.showingContacts.filter(
    c =>
      !c.isAgent && (mightAttendStatus.includes(c.confirmationStatus) || canceledStatus.includes(c.confirmationStatus))
  )
}

/**
 * For a showing, return true iff the agent who owns the calendar was NOT_AVAIL
 */
function wasAgentUnavailable(
  showing: FetchAgentCalendarQuery['agentCalendar']['showings'][0],
  contactId: number
): boolean {
  return showing.showingContacts.some(
    c => c.contact.id === contactId && c.confirmationStatus === ConfirmationStatus.NOT_AVAIL
  )
}

/**
 * Of all the prospects on a showing, get the "most confirmed" prospect,
 * which should match the GSuite / Outlook calendar event title. E.g. if we have
 *
 * Jim B · CANCEL_BY_US · updated 10m ago
 * Carl L · CONFIRMED · updated 4d ago
 * Sally A · CONFIRMED · updated 1d ago
 *
 * ... then we return { Carl L }
 */
function getMostLikelyProspect(
  showingContacts: FetchAgentCalendarQuery['agentCalendar']['showings'][0]['showingContacts']
): FetchAgentCalendarQuery['agentCalendar']['showings'][0]['showingContacts'][0] | undefined {
  const prospects = showingContacts
    .filter(c => !c.isAgent)
    ?.slice()
    .sort(
      (
        a: FetchAgentCalendarQuery['agentCalendar']['showings'][0]['showingContacts'][0],
        b: FetchAgentCalendarQuery['agentCalendar']['showings'][0]['showingContacts'][0]
        // eslint-disable-next-line arrow-body-style
      ) => {
        return a.confirmationStatus === b.confirmationStatus
          ? compareDates(a.timeUpdated, b.timeUpdated)
          : computeSortOrder(a) - computeSortOrder(b)
      }
    )
  const mightAttend = prospects.filter(c => !c.isAgent && mightAttendStatus.includes(c.confirmationStatus))
  const canceled = prospects.filter(c => !c.isAgent && canceledStatus.includes(c.confirmationStatus))

  const onlyCanceledProspects = typeof mightAttend === undefined || mightAttend.length === 0
  return onlyCanceledProspects ? canceled.shift() : mightAttend.shift()
}

/**
 * Sort attending contacts by "most likely to attend" with non-null names first, agents last.
 * The goal is to surface prospects who are likeliest to attend and be memorable.
 */
function computeSortOrder(sc: FetchAgentCalendarQuery['agentCalendar']['showings'][0]['showingContacts'][0]) {
  const ix = showingStatusSort.indexOf(sc.confirmationStatus.toString())
  if (ix < 0) {
    console.log('Unexpected confirmationStatus ' + sc.confirmationStatus)
    return -1
  }
  return (ix + 1) * 2 + (sc.isAgent ? 1 : 0) + (sc.contact.firstName === null ? 1 : 0)
}

/**
 * Each calendar element has its own type:
 *
 * - OFF_HOURS: the agent is * not * working
 * - WORKING: agent is working and can do showings
 * - SHOWING: Lisa showing as seen in CC
 * - BLOCKED: GSuite / Exchange event marked 'busy', not a Lisa showing
 * - FREE: GSuite / Exchange event marked 'free', not a Lisa showing
 */
export enum ElemType {
  OFF_HOURS,
  WORKING,
  SHOWING,
  BLOCKED,
  FREE,
}

interface CalendarBlockProps {
  time: string
  hour: number
}

/**
 * The basic unit of the calendar grid background, looks like this:
 *
 * 1pm   [hour element root: 48px tall, white, no content]
 *       [hour element separator: 1px tall, grey, no content]
 */
function HourElement(props: CalendarBlockProps) {
  return (
    <FlexDiv>
      <HourElementText>{props.time}</HourElementText>
      <div className={wrapCalElementClass}>
        <div className={hourElementClass(props.hour)} />
        <HourElementSeparatorRoot />
      </div>
    </FlexDiv>
  )
}

/**
 * It's surprisingly hard to know when a Lisa showing will end. Each showing has a `timeStart`,
 * but to calc the `timeEnd` you gotta fetch two levels of "showingDurationMins" from the task/firm.
 *
 * Defaults to 20 mins if the task and firm are both null.
 */
function getShowingDurationMins(task: FetchAgentCalendarQuery['agentCalendar']['showings'][0]['leasingTask']) {
  const taskShowingDurationMins = task.leasingInfo && task.leasingInfo.showingDurationMins
  const firmShowingDurationMins = task.firm && task.firm.showingDurationMins
  return taskShowingDurationMins || firmShowingDurationMins || 20
}

/**
 * Hydrates server-rendered widget.
 */
export function hydrateAgentCalendarWidget() {
  // Only hydrate widget if it's actually rendered
  if (document.getElementById(agentCalendarWidgetId) == null) {
    return
  }
  agentCalendarWidget.init(appContext().hydrateProps)
}

/**
 * Returns the normalized string of the email provider.
 */
export function getEventSrc(source: string | null | undefined) {
  if (!source) {
    return '(no Cal source)'
  }
  switch (source) {
    case 'GOOGLE':
      return 'Gmail'
    case 'EXCHANGE':
      return 'Outlook'
    case 'APM':
      return 'APM'
    default:
      return capitalize(source)
  }
}

export function getShowingModalChildren(
  showing: FetchAgentCalendarQuery['agentCalendar']['showings'][0],
  timeZone: string,
  agentId: number | undefined,
  emailProvider?: string | null,
  isDeclined?: boolean
) {
  const mostLikelyProspect = getMostLikelyProspect(showing.showingContacts)?.contact
  const acceptedProspects = showing.showingContacts.filter(
    p => willAttendStatus.includes(p.confirmationStatus) && !p.isAgent
  )

  if (mostLikelyProspect == null) {
    return <></>
  }

  const numOthers = acceptedProspects.length - 1
  const otherStr = numOthers > 0 ? ' + ' + nPlural(numOthers, 'other') : ''
  const lastName =
    mostLikelyProspect?.lastName === null
      ? nPlural(acceptedProspects.length, 'prospect')
      : mostLikelyProspect?.lastName + otherStr
  const addressParts = showing.leasingTask.address?.addressParts

  return (
    <div>
      <IconAndTextWrapper>
        <div className={iconBoxClass({ marginTop: '-15' })}>
          <Img src="/leasing/assets/lisa-icon.svg" />
        </div>
        <div>
          {/* main prospect's name as title */}
          <ModalFirstTitle>{mostLikelyProspect?.firstName ? mostLikelyProspect.firstName + ' ' : ''}</ModalFirstTitle>
          <ModalSecondTitle>{lastName}</ModalSecondTitle>
          {/* date and time of showing */}
          <ModalDateTime>{getEventTimePeriodStr(showing.timeStart, 30, timeZone)}</ModalDateTime>
        </div>
      </IconAndTextWrapper>

      <IconAndTextWrapper>
        <div className={iconBoxClass({ marginTop: '10' })}>
          {tourTypeToIcon(showing.tourType || TourType.IN_PERSON, 'silver', 'medium')}
        </div>
        {/* e.g. In-person · Flamingo Point Center Tower
         *       1504 Bay Rd, Miami Beach FL
         */}
        <PropertyInfo>
          {showing.tourType && (
            <ShowingTourType>{capitalize(showing.tourType.valueOf().replace('_', '-'))}</ShowingTourType>
          )}
          {' · ' + showing.leasingTask.displayName}
          <PropertyAddress>
            {addressParts && formatAddress(addressParts.street, addressParts.city, addressParts.state)}
          </PropertyAddress>
        </PropertyInfo>
      </IconAndTextWrapper>

      <IconAndTextWrapper>
        <div className={iconBoxClass({})}>
          ‍<Icon name="floorplan" color="silver" size="medium" />
        </div>
        {/* e.g. 1 bedroom · 2 accepted prospects */}
        <PropertyInfo>
          <UnitInfo>
            {unitTypeToStr(showing.unitType, showing.leasingTask) +
              ' · ' +
              nPlural(acceptedProspects.length, 'accepted prospect')}
          </UnitInfo>
        </PropertyInfo>
      </IconAndTextWrapper>

      {/* for each attending prospect, we show their name, confirmation status, contact information */}
      <ModalDetails>
        {acceptedProspects.map((p, i) => (
          <IconAndTextWrapper key={i}>
            <div className={iconBoxClass({})}></div>
            <div>
              <Link href={getTranscriptUrl(p.contact, showing.leasingTask.id)} target="_blank">
                {p.contact.firstName && p.contact.lastName
                  ? [p.contact.firstName, p.contact.lastName].join(' ')
                  : '(no name)'}
              </Link>
              {' · ' + getConfirmationStatusStr(p.confirmationStatus)}
              <SmallNote>{getPreferredContactInfo(p.contact)}</SmallNote>
            </div>
          </IconAndTextWrapper>
        ))}
      </ModalDetails>

      {/* if agent has declined this showing, display that in the modal */}
      {isDeclined && (
        <IconAndTextWrapper>
          <div className={iconBoxClass({ marginTop: '5' })}>
            <Icon name="close" color="coral" size="medium" />
          </div>
          <DeclinedTime showing={showing} emailProvider={emailProvider} agentId={agentId} timezone={timeZone} />
        </IconAndTextWrapper>
      )}
    </div>
  )
}

function formatAddress(street: string, city: string, state: string) {
  return `${street}, ${city}, ${state}`
}

export function getFreeBusyModalChildren(
  startTime: string,
  endTime: string,
  timeZone: string,
  elemType: ElemType,
  dayBeginning: string,
  dayEnd: string,
  allCalEvents?: FetchAgentCalendarQuery['agentCalendar']['calEvents']
) {
  if (allCalEvents == null) {
    return <></>
  }

  // make a copy of allCalEvents
  const calEvents = allCalEvents.map(e => Object.assign({}, e))

  // if agents have events that extend more than a day, we want to only show the time frame for the present day
  const modifiedCalEvents = calEvents.map(event => {
    if (isAfter(event.timeEnd, dayEnd)) {
      event.timeEnd = dayEnd
    }
    if (isAfter(dayBeginning, event.timeStart)) {
      event.timeStart = dayBeginning
    }
    return event
  })

  const relevantEvents = modifiedCalEvents.filter(
    e =>
      (e.timeStart === startTime || isAfter(e.timeStart, startTime)) &&
      (e.timeEnd === endTime || isAfter(endTime, e.timeEnd))
  )

  const emailProvider = getEventSrc(modifiedCalEvents[0].source)
  const calProvider = emailProvider === 'Gmail' ? 'Google Calendar' : emailProvider

  const links = getBlockUnblockLinks(elemType, modifiedCalEvents[0].source)
  return (
    <div>
      <IconAndTextWrapper>
        <div className={iconBoxClass({ marginTop: '-15' })}>
          <span className={freeBusyIconClass({ elemType })} />
        </div>
        <div>
          {/* e.g. Free in Outlook */}
          <ModalFirstTitle>{elemType === ElemType.BLOCKED ? 'Busy' : 'Free'}</ModalFirstTitle>
          {calProvider && <ModalSecondTitle>{' in ' + calProvider}</ModalSecondTitle>}
          {/* date and time of busy/free block */}
          <ModalDateTime>{getEventTimePeriodStr(startTime, diffMins(endTime, startTime), timeZone)}</ModalDateTime>
        </div>
      </IconAndTextWrapper>

      <ModalDetails>
        {relevantEvents.map((e, i) => (
          <IconAndTextWrapper key={i}>
            <div className={iconBoxClass({})}></div>
            {timeFmt(e.timeStart, timeZone) + ' - ' + timeFmt(e.timeEnd, timeZone)}
            {separator}
            {'added ' + getTimeAgo(e.timeCreated, new Date())}
          </IconAndTextWrapper>
        ))}
      </ModalDetails>
      <br />
      <IconAndTextWrapper>
        <div className={iconBoxClass({})}></div>
        <SmallNote>Want to edit this calendar block?</SmallNote>
      </IconAndTextWrapper>
      <IconAndTextWrapper>
        <div className={iconBoxClass({})}></div>
        {links}
      </IconAndTextWrapper>
    </div>
  )
}

/**
 * Returns a div with two links in it that redirect the user to pages about blocking/unblocking their calendar.
 */
function getBlockUnblockLinks(elemType: ElemType, emailProvider?: string | null) {
  const guideFor = elemType === ElemType.BLOCKED ? 'Unblocking' : 'Blocking'
  let providerName
  let hoursHref
  let daysHref

  switch (emailProvider) {
    case 'GOOGLE':
      providerName = 'Google Calendar'
      hoursHref =
        elemType === ElemType.BLOCKED
          ? 'https://support.dynasty.com/article/g00zx8rt55-unblocking-hours-in-google-calendar'
          : 'https://support.dynasty.com/article/f7dmncetps-blocking-hours-for-google-calendars'
      daysHref =
        elemType === ElemType.BLOCKED
          ? 'https://support.dynasty.com/article/dj7qq3a9kf-unblocking-days-in-google-calendar'
          : 'https://support.dynasty.com/article/rvvgjvtuab-blocking-days-in-google-calendar'
      break
    case 'EXCHANGE':
      providerName = 'Microsoft Outlook'
      hoursHref =
        elemType === ElemType.BLOCKED
          ? 'https://support.dynasty.com/article/pn4r7ari44-unblocking-hours-in-microsoft-outlook'
          : 'https://support.dynasty.com/article/wqfytr8dke-blocking-hours-for-outlook'
      daysHref =
        elemType === ElemType.BLOCKED
          ? 'https://support.dynasty.com/article/0o8rsvmfer-unblocking-days-in-microsoft-outlook'
          : 'https://support.dynasty.com/article/g7huiqkgwv-blocking-days-in-outlook'
      break
    default:
      return <></>
  }

  return (
    <div>
      <Link href={hoursHref} target="_blank">
        {guideFor + ' Hours in ' + providerName + ' »'}
      </Link>
      <br />
      <Link href={daysHref} target="_blank">
        {guideFor + ' Days in ' + providerName + ' »'}
      </Link>
    </div>
  )
}

const declinedStatus = [
  ConfirmationStatus.CANCEL_BY_THEM,
  ConfirmationStatus.CANCEL_BY_THEM_NOSYNC,
  ConfirmationStatus.NOT_AVAIL,
  ConfirmationStatus.REJECTED,
]

function DeclinedTime(props: {
  showing: FetchAgentCalendarQuery['agentCalendar']['showings'][0]
  emailProvider?: string | null
  agentId: number | undefined
  timezone: string
}) {
  const { showing, emailProvider, agentId, timezone } = props
  const hist = showing.history.find(h => h.contactId === agentId && declinedStatus.includes(h.confirmationStatus))
  return (
    <PropertyInfo>
      <span className={declinedClass({})}>Declined</span>
      <span className={declinedClass({ isSource: true })}> in {emailProvider}</span>
      {agentId && hist?.timeUpdated && (
        <PropertyAddress>
          {getTimeAgo(hist.timeUpdated, new Date())}
          {separator}
          {dateNoYearFmt(hist.timeUpdated, timezone)} at {timeFmt(hist.timeUpdated, timezone)}
        </PropertyAddress>
      )}
    </PropertyInfo>
  )
}

/**
 * Returns an element containing the contact's phone number or email address based on what they prefer.
 */
function getPreferredContactInfo(
  contact: FetchAgentCalendarQuery['agentCalendar']['showings'][0]['showingContacts'][0]['contact']
) {
  if (contact.preferMessageType === MessageType.SMS && contact.addressPhone) {
    return formatPhoneNumber(contact.addressPhone)
  } else if (contact.preferMessageType === MessageType.EMAIL && contact.addressEmail) {
    return contact.addressEmail
  }
  return null
}

function getTranscriptUrl(
  contact: FetchAgentCalendarQuery['agentCalendar']['showings'][0]['showingContacts'][0]['contact'],
  taskId: number
) {
  return contact.threads.find(t => t.leasingTask.id === taskId)?.transcriptUrl
}

function getConfirmationStatusStr(status: ConfirmationStatus) {
  switch (status) {
    case 'ACCEPTED':
    case 'CONFIRM_SENT':
      return 'accepted, unconfirmed'
    case 'CONFIRMED':
      return 'confirmed'
    default:
      return '_'
  }
}

export function getEventTimePeriodStr(startTime: string, durationMins: number, zone: string) {
  const dateStr = dateNoYearFmtLong(startTime, zone)
  return (
    <>
      {dateStr}
      {separator}
      {timeFmt(startTime, zone) + ' — ' + timeFmt(addMinutes(startTime, durationMins), zone)}
    </>
  )
}

function unitTypeToStr(
  unitType: UnitType | null | undefined,
  task: FetchAgentCalendarQuery['agentCalendar']['showings'][0]['leasingTask']
) {
  if (task.leasingInfo?.propertyType === LeasePropertyType.MF) {
    switch (unitType) {
      case UnitType.STUDIO:
        return 'studio'
      case UnitType.BR1:
        return '1 bedroom'
      case UnitType.BR2:
        return '2 bedroom'
      case UnitType.BR3:
        return '3 bedroom'
      case UnitType.BR4:
        return '4 bedroom'
      case UnitType.BR5PLUS:
        return '5+ bedroom'
    }
  }
  // use floorplan -> bedrooms for single family homes
  if (task.floorPlans.length === 0) {
    return '_'
  }
  return task.floorPlans[0].bedrooms + ' bedroom'
}

const separator = (
  <>
    <Nbsp />
    <Nbsp />
    {'·'}
    <Nbsp />
    <Nbsp />
  </>
)
